// ============================================================
// PortalRegistry — the saved-portals list surface.
//
// Lists REAL saved portals (sq_project_portals) with type, readiness,
// enabled-module count, last-updated, and receipt count. Honest empty /
// loading / auth-required / backend-not-migrated states — never a
// fabricated portal row. Presentational + SSR-safe; the route wrapper owns
// the usePortals hook + navigation.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  PROJECT_TYPE_META, OLIVIUM_ACCENT, type PortalRecord, type ProjectType,
} from './portalFactory'
import { PORTAL_HONESTY, ReadinessPill, StateBlock, BackendNotReadyBanner, primaryBtn, chipBtn } from './PortalKit'

export interface PortalRegistryProps {
  portals: PortalRecord[]
  loading: boolean
  error: string | null
  authRequired: boolean
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenFactory?: () => void
  onCreatePortal?: () => void
  onOpenPortal?: (id: string) => void
}

function fmtDate(iso: string): string {
  // Stable across SSR/CSR — slice the ISO date rather than locale-format.
  return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : '—'
}

export default function PortalRegistry({ portals, loading, error, authRequired, onHome, onBackCommandCenter, onOpenFactory, onCreatePortal, onOpenPortal }: PortalRegistryProps) {
  const [typeFilter, setTypeFilter] = useState<ProjectType | 'all'>('all')
  const filtered = typeFilter === 'all' ? portals : portals.filter((p) => p.project_type === typeFilter)
  const backendNotReady = !!error && /not migrated/i.test(error)

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Portal Factory', onClick: onOpenFactory }}
      breadcrumb={['OLIVIUM', 'PORTAL FACTORY', 'REGISTRY']}
      title="Portal Registry"
      titleAccent={OLIVIUM_ACCENT}
      honestyBadge={PORTAL_HONESTY}
      contextBar={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: CC_INK.muted }}>Saved project portals</span>
          <button type="button" onClick={() => setTypeFilter('all')} style={{ ...chipBtn, ...(typeFilter === 'all' ? { color: '#b794f6', borderColor: OLIVIUM_ACCENT + '55' } : {}) }}>All</button>
          {(Object.keys(PROJECT_TYPE_META) as ProjectType[]).map((pt) => (
            <button key={pt} type="button" onClick={() => setTypeFilter(pt)} style={{ ...chipBtn, ...(typeFilter === pt ? { color: '#b794f6', borderColor: OLIVIUM_ACCENT + '55' } : {}) }}>
              {PROJECT_TYPE_META[pt].label}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button type="button" style={primaryBtn} onClick={onCreatePortal}>+ Create a project portal</button>
        </div>
      }
    >
      <div>
        {backendNotReady && <BackendNotReadyBanner message={error!} />}

        {loading && <StateBlock kind="loading" title="Loading saved portals…" />}

        {!loading && authRequired && (
          <StateBlock kind="auth" title="Sign in to load your saved portals" body="Portals are scoped to your authenticated cockpit session." />
        )}

        {!loading && !authRequired && error && !backendNotReady && (
          <StateBlock kind="error" title="Could not load portals" body={error} />
        )}

        {!loading && !authRequired && !error && filtered.length === 0 && (
          <StateBlock
            kind="empty"
            title={portals.length === 0 ? 'No portals yet' : 'No portals match this filter'}
            body={portals.length === 0 ? 'Create your first project portal — nothing is seeded here.' : 'Try a different project type.'}
          />
        )}

        {!loading && !authRequired && filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onOpenPortal?.(p.id)}
                style={{ textAlign: 'left', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <strong style={{ fontSize: 14, color: CC_INK.primary, flex: 1 }}>{p.project_name}</strong>
                  <ReadinessPill status={p.readiness_status} />
                </div>
                <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginBottom: 10 }}>
                  {PROJECT_TYPE_META[p.project_type]?.label ?? p.project_type} · {p.visibility_status} · {p.project_slug}
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 10, color: CC_INK.muted, fontFamily: CC_MONO }}>
                  <span>{`◧ ${p.counts?.modules ?? 0} modules`}</span>
                  <span>{`◦ ${p.counts?.open_actions ?? 0} open`}</span>
                  <span>{`⚑ ${p.counts?.open_blockers ?? 0} blockers`}</span>
                  <span>{`✓ ${p.counts?.receipts ?? 0} receipts`}</span>
                </div>
                <div style={{ marginTop: 10, fontSize: 10, color: CC_INK.faint }}>{`Updated ${fmtDate(p.updated_at)}`}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </CommandCenterShell>
  )
}
